import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Truck,
  ArrowLeft,
  MapPin,
  Package,
  Users,
  AlertTriangle,
  Building2,
  ArrowUpDown,
  Check,
  Bike,
  Car,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import DestinationPicker from "@/components/DestinationPicker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VEHICLE_ICON = {
  moto: Bike,
  tricycle: Car,
  fourgon: Truck,
  camion: Truck,
  semi_remorque: Truck,
};

const PAYMENT_METHODS = [
  { key: "cash", label: "Espèces à la livraison" },
  { key: "wave", label: "Wave" },
  { key: "orange_money", label: "Orange Money" },
  { key: "free_money", label: "Free Money" },
];

export default function CargoOrder({ user, onLogout }) {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [vehicleType, setVehicleType] = useState("");
  const [volumeM3, setVolumeM3] = useState("");
  const [weightKg, setWeightKg] = useState("");
  const [helpersCount, setHelpersCount] = useState(0);
  const [hasFragile, setHasFragile] = useState(false);
  const [pickupFloor, setPickupFloor] = useState(0);
  const [deliveryFloor, setDeliveryFloor] = useState(0);
  const [hasElevator, setHasElevator] = useState(true);
  const [notes, setNotes] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");

  const [pickup, setPickup] = useState(null); // {lat, lng, address}
  const [dropoff, setDropoff] = useState(null);
  const [showPicker, setShowPicker] = useState(null); // 'pickup' | 'dropoff'

  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/cargo/vehicles`).then((r) => setVehicles(r.data)).catch((err) => {
      console.warn("Failed to load vehicles:", err);
    });
  }, []);

  const fetchQuote = useCallback(async () => {
    if (!vehicleType || !pickup || !dropoff) {
      setQuote(null);
      return;
    }
    try {
      const res = await axios.post(`${API}/cargo/quote`, {
        vehicle_type: vehicleType,
        pickup_latitude: pickup.lat,
        pickup_longitude: pickup.lng,
        delivery_latitude: dropoff.lat,
        delivery_longitude: dropoff.lng,
        volume_m3: parseFloat(volumeM3) || 0,
        weight_kg: parseFloat(weightKg) || 0,
        helpers_count: helpersCount,
        has_fragile: hasFragile,
        pickup_floor: pickupFloor,
        delivery_floor: deliveryFloor,
        has_elevator: hasElevator,
      });
      setQuote(res.data);
      setQuoteError("");
    } catch (err) {
      setQuote(null);
      setQuoteError(err.response?.data?.detail || "Erreur devis");
    }
  }, [
    vehicleType, pickup, dropoff, volumeM3, weightKg,
    helpersCount, hasFragile, pickupFloor, deliveryFloor, hasElevator,
  ]);

  useEffect(() => {
    fetchQuote();
  }, [fetchQuote]);

  const submit = async () => {
    if (!quote) {
      toast.error("Veuillez compléter le formulaire pour obtenir un devis");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/cargo/orders`, {
        vehicle_type: vehicleType,
        customer_id: user.id,
        customer_name: user.full_name || user.email,
        customer_phone: user.phone || "",
        pickup_address: pickup.address,
        pickup_latitude: pickup.lat,
        pickup_longitude: pickup.lng,
        delivery_address: dropoff.address,
        delivery_latitude: dropoff.lat,
        delivery_longitude: dropoff.lng,
        volume_m3: parseFloat(volumeM3) || 0,
        weight_kg: parseFloat(weightKg) || 0,
        helpers_count: helpersCount,
        has_fragile: hasFragile,
        pickup_floor: pickupFloor,
        delivery_floor: deliveryFloor,
        has_elevator: hasElevator,
        notes,
        payment_method: paymentMethod,
      });
      toast.success(res.data.message);
      navigate(`/tracking?id=${res.data.order_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de création");
    } finally {
      setLoading(false);
    }
  };

  const fmt = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(n || 0));

  return (
    <div className="min-h-screen bg-[#101010] text-white pb-28">
      {/* HEADER */}
      <header className="sticky top-0 z-30 bg-[#101010]/95 backdrop-blur border-b border-[#2D2D2D] px-4 py-4 flex items-center gap-3">
        <button
          onClick={() => navigate("/home")}
          className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#1A1A1A] transition-colors"
          data-testid="cargo-back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-xl font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>
            Cargo & Déménagement
          </h1>
          <p className="text-xs text-[#A0A0A0]">Transport lourd sur mesure</p>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-5 space-y-5">
        {/* 1. VEHICLE */}
        <section>
          <h2 className="text-sm font-semibold text-[#A0A0A0] mb-2 uppercase tracking-wider">
            1. Type de véhicule
          </h2>
          <div className="grid grid-cols-2 gap-2" data-testid="cargo-vehicle-grid">
            {vehicles.map((v) => {
              const Icon = VEHICLE_ICON[v.key] || Truck;
              const selected = vehicleType === v.key;
              return (
                <button
                  key={v.key}
                  onClick={() => setVehicleType(v.key)}
                  className={`p-4 rounded-2xl border-2 text-left transition-all active:scale-[0.98] ${
                    selected
                      ? "border-[#D4AF37] bg-[#003594]/10"
                      : "border-[#2D2D2D] bg-[#1A1A1A] hover:border-[#003594]/60"
                  }`}
                  data-testid={`vehicle-${v.key}`}
                >
                  <div className="flex items-center justify-between">
                    <Icon
                      className="w-6 h-6"
                      style={{ color: selected ? "#D4AF37" : "#003594" }}
                      strokeWidth={1.8}
                    />
                    {selected && <Check className="w-5 h-5 text-[#D4AF37]" />}
                  </div>
                  <p className="font-semibold mt-2">{v.label}</p>
                  <p className="text-xs text-[#A0A0A0] mt-1 leading-snug line-clamp-2">
                    {v.description}
                  </p>
                  <p className="text-xs text-[#D4AF37] mt-1.5 font-medium">
                    Base {fmt(v.base_fee)} FCFA + {fmt(v.per_km)}/km
                  </p>
                </button>
              );
            })}
          </div>
        </section>

        {/* 2. ADDRESSES */}
        <section>
          <h2 className="text-sm font-semibold text-[#A0A0A0] mb-2 uppercase tracking-wider">
            2. Itinéraire
          </h2>
          <div className="space-y-2">
            <button
              onClick={() => setShowPicker("pickup")}
              className="w-full bg-[#1A1A1A] hover:bg-[#242424] border border-[#2D2D2D] rounded-2xl p-4 flex items-center gap-3 text-left transition-all"
              data-testid="cargo-pickup-btn"
            >
              <div className="w-10 h-10 rounded-full bg-[#003594]/20 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-[#A0A0A0]">Départ</p>
                <p className="text-sm font-medium truncate">
                  {pickup?.address || "Sélectionner le lieu de ramassage"}
                </p>
              </div>
            </button>
            <button
              onClick={() => setShowPicker("dropoff")}
              className="w-full bg-[#1A1A1A] hover:bg-[#242424] border border-[#2D2D2D] rounded-2xl p-4 flex items-center gap-3 text-left transition-all"
              data-testid="cargo-dropoff-btn"
            >
              <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-[#A0A0A0]">Destination</p>
                <p className="text-sm font-medium truncate">
                  {dropoff?.address || "Sélectionner la destination"}
                </p>
              </div>
            </button>
          </div>
        </section>

        {/* 3. DÉTAILS */}
        <section>
          <h2 className="text-sm font-semibold text-[#A0A0A0] mb-2 uppercase tracking-wider">
            3. Volume & détails
          </h2>
          <div className="bg-[#1A1A1A] border border-[#2D2D2D] rounded-2xl p-4 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#A0A0A0] block mb-1.5">Volume (m³)</label>
                <Input
                  type="number"
                  min={0}
                  step={0.5}
                  value={volumeM3}
                  onChange={(e) => setVolumeM3(e.target.value)}
                  placeholder="Ex: 5"
                  className="bg-[#101010] border-[#2D2D2D] text-white"
                  data-testid="cargo-volume-input"
                />
              </div>
              <div>
                <label className="text-xs text-[#A0A0A0] block mb-1.5">Poids (kg)</label>
                <Input
                  type="number"
                  min={0}
                  value={weightKg}
                  onChange={(e) => setWeightKg(e.target.value)}
                  placeholder="Ex: 200"
                  className="bg-[#101010] border-[#2D2D2D] text-white"
                  data-testid="cargo-weight-input"
                />
              </div>
            </div>

            <div>
              <label className="text-xs text-[#A0A0A0] block mb-1.5 flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5" />
                Aides au chargement/déchargement
              </label>
              <div className="flex items-center gap-2" data-testid="helpers-counter">
                {[0, 1, 2, 3, 4].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setHelpersCount(n)}
                    className={`flex-1 py-2.5 rounded-xl border font-semibold text-sm transition-all ${
                      helpersCount === n
                        ? "border-[#D4AF37] bg-[#003594]/20 text-[#D4AF37]"
                        : "border-[#2D2D2D] bg-[#101010] text-white hover:border-[#003594]/60"
                    }`}
                    data-testid={`helper-${n}`}
                  >
                    {n === 0 ? "Aucune" : `+${n}`}
                  </button>
                ))}
              </div>
              {helpersCount > 0 && (
                <p className="text-xs text-[#A0A0A0] mt-1.5">
                  5 000 FCFA / aide ({fmt(helpersCount * 5000)} FCFA total)
                </p>
              )}
            </div>

            <label
              className="flex items-center gap-3 py-2 cursor-pointer select-none"
              data-testid="fragile-toggle"
            >
              <input
                type="checkbox"
                checked={hasFragile}
                onChange={(e) => setHasFragile(e.target.checked)}
                className="w-5 h-5 accent-[#003594]"
              />
              <AlertTriangle className="w-4 h-4 text-yellow-400" />
              <span className="text-sm">
                Articles fragiles (+ 2 500 FCFA)
              </span>
            </label>

            <div className="border-t border-[#2D2D2D] pt-3">
              <label
                className="flex items-center gap-3 py-1 cursor-pointer select-none"
                data-testid="elevator-toggle"
              >
                <input
                  type="checkbox"
                  checked={hasElevator}
                  onChange={(e) => setHasElevator(e.target.checked)}
                  className="w-5 h-5 accent-[#003594]"
                />
                <Building2 className="w-4 h-4 text-[#A0A0A0]" />
                <span className="text-sm">Ascenseur disponible</span>
              </label>
              {!hasElevator && (
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div>
                    <label className="text-xs text-[#A0A0A0] flex items-center gap-1.5 mb-1.5">
                      <ArrowUpDown className="w-3.5 h-3.5" /> Étage départ
                    </label>
                    <Input
                      type="number"
                      min={0}
                      max={30}
                      value={pickupFloor}
                      onChange={(e) => setPickupFloor(parseInt(e.target.value) || 0)}
                      className="bg-[#101010] border-[#2D2D2D] text-white"
                      data-testid="pickup-floor-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-[#A0A0A0] flex items-center gap-1.5 mb-1.5">
                      <ArrowUpDown className="w-3.5 h-3.5" /> Étage destination
                    </label>
                    <Input
                      type="number"
                      min={0}
                      max={30}
                      value={deliveryFloor}
                      onChange={(e) => setDeliveryFloor(parseInt(e.target.value) || 0)}
                      className="bg-[#101010] border-[#2D2D2D] text-white"
                      data-testid="delivery-floor-input"
                    />
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="text-xs text-[#A0A0A0] block mb-1.5">Notes (optionnel)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Précisions sur le chargement, objets particuliers..."
                rows={2}
                className="w-full bg-[#101010] border border-[#2D2D2D] rounded-xl p-3 text-sm text-white placeholder:text-[#A0A0A0] resize-none focus:border-[#003594] outline-none"
                data-testid="cargo-notes"
              />
            </div>
          </div>
        </section>

        {/* 4. PAYMENT */}
        <section>
          <h2 className="text-sm font-semibold text-[#A0A0A0] mb-2 uppercase tracking-wider">
            4. Paiement
          </h2>
          <div className="grid grid-cols-2 gap-2">
            {PAYMENT_METHODS.map((p) => (
              <button
                key={p.key}
                onClick={() => setPaymentMethod(p.key)}
                className={`py-3 rounded-xl border font-medium text-sm transition-all ${
                  paymentMethod === p.key
                    ? "border-[#D4AF37] bg-[#003594]/10 text-[#D4AF37]"
                    : "border-[#2D2D2D] bg-[#1A1A1A] text-white hover:border-[#003594]/60"
                }`}
                data-testid={`payment-${p.key}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </section>

        {/* QUOTE */}
        {quoteError && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-4 text-red-400 text-sm" data-testid="cargo-quote-error">
            {quoteError}
          </div>
        )}
        {quote && (
          <section
            className="bg-gradient-to-br from-[#003594] to-[#D4AF37] rounded-2xl p-5 text-white shadow-lg shadow-[#003594]/30"
            data-testid="cargo-quote-card"
          >
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm opacity-90">Devis estimatif</p>
              <Package className="w-5 h-5" />
            </div>
            <p className="text-xs opacity-80">Distance {quote.distance_km} km</p>
            <p className="text-4xl font-black mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
              {fmt(quote.total_amount)} <span className="text-lg font-semibold">FCFA</span>
            </p>
            <div className="mt-3 pt-3 border-t border-white/20 space-y-1 text-sm">
              <div className="flex justify-between opacity-90">
                <span>Tarif de base ({quote.vehicle_label})</span>
                <span>{fmt(quote.breakdown.base_fee)}</span>
              </div>
              <div className="flex justify-between opacity-90">
                <span>Distance</span>
                <span>{fmt(quote.breakdown.distance_cost)}</span>
              </div>
              {quote.breakdown.helpers_cost > 0 && (
                <div className="flex justify-between opacity-90">
                  <span>Aides</span>
                  <span>+{fmt(quote.breakdown.helpers_cost)}</span>
                </div>
              )}
              {quote.breakdown.fragile_surcharge > 0 && (
                <div className="flex justify-between opacity-90">
                  <span>Fragile</span>
                  <span>+{fmt(quote.breakdown.fragile_surcharge)}</span>
                </div>
              )}
              {quote.breakdown.floors_surcharge > 0 && (
                <div className="flex justify-between opacity-90">
                  <span>Étages ({quote.breakdown.extra_floors})</span>
                  <span>+{fmt(quote.breakdown.floors_surcharge)}</span>
                </div>
              )}
            </div>
          </section>
        )}
      </main>

      {/* STICKY CTA */}
      <div className="fixed bottom-0 inset-x-0 bg-[#1A1A1A] border-t border-[#2D2D2D] p-4 z-20">
        <Button
          onClick={submit}
          disabled={loading || !quote || !pickup || !dropoff || !vehicleType}
          className="w-full bg-gradient-to-r from-[#003594] to-[#D4AF37] hover:from-[#003380] hover:to-[#0099BF] disabled:opacity-40 text-white font-bold rounded-2xl py-4 text-base shadow-lg shadow-[#003594]/30"
          data-testid="cargo-submit-btn"
        >
          {loading
            ? "Création..."
            : quote
            ? `Confirmer — ${fmt(quote.total_amount)} FCFA`
            : "Complétez le formulaire"}
        </Button>
      </div>

      {/* PICKER */}
      {showPicker && (
        <DestinationPicker
          onClose={() => setShowPicker(null)}
          onConfirm={(lat, lng, address) => {
            if (showPicker === "pickup") {
              setPickup({ lat, lng, address });
            } else {
              setDropoff({ lat, lng, address });
            }
            setShowPicker(null);
          }}
        />
      )}
    </div>
  );
}
